/**
 * Platform-level explainability engine.
 * 
 * Provides explainability views for entire snapshots (not just individual items).
 * Builds ExecutivePriorityView and TracePriorityView for snapshots using
 * PriorityTruth's in-memory structures (events, snapshots, tool runs, criteria, lenses).
 */

import type { PrioritySnapshot } from "./priorityTruth";
import type { Item } from "../harmonizer/types";
import {
  buildExecutivePriorityView,
  buildTracePriorityView,
  type ExecutivePriorityView,
  type TracePriorityView,
} from "../arrange/explainability";
import type { ArrangeAuditEvent } from "../arrange/audit";

/**
 * Builds an executive priority view for an entire snapshot.
 * 
 * Aggregates high-level information about the snapshot's decisions:
 * - Summary statistics
 * - Top-ranked items
 * - Tool and configuration used
 * 
 * @param snapshot - Priority snapshot to explain
 * @param items - Items in the snapshot (for labels)
 * @param events - Audit events for the session
 * @returns Executive priority view for the snapshot
 */
export function buildSnapshotExecutiveView(
  snapshot: PrioritySnapshot,
  _items: Item[],
  _events: ArrangeAuditEvent[]
): ExecutivePriorityView {
  // Find the snapshot's score set
  const scoreSet = snapshot.scoreSet;

  // Build a representative executive view (using the top-ranked item as representative)
  const topItem = scoreSet.scores
    .filter((s) => s.rank !== undefined)
    .sort((a, b) => (a.rank || 0) - (b.rank || 0))[0];

  if (!topItem) {
    // Fallback: use first item if no ranks
    const firstItem = scoreSet.scores[0];
    if (firstItem) {
      return buildExecutivePriorityView(firstItem.itemId, scoreSet);
    }
    // Empty snapshot
    return {
      itemId: "",
      rank: null,
      numericScore: null,
      categoryId: null,
      toolName: snapshot.toolName,
      keyDrivers: [],
    };
  }

  // Build executive view for the top item (as representative of the snapshot)
  const itemView = buildExecutivePriorityView(topItem.itemId, scoreSet);

  // Enhance with snapshot-level information
  return {
    ...itemView,
    // Use snapshot-level label if available
    label: `Snapshot: ${snapshot.toolName} (${snapshot.createdAt})`,
  };
}

/**
 * Builds a trace priority view for an entire snapshot.
 * 
 * Provides complete audit trail for the snapshot:
 * - All events related to the snapshot's creation
 * - Tool run details
 * - Configuration and context
 * 
 * @param snapshot - Priority snapshot to trace
 * @param items - Items in the snapshot
 * @param events - Audit events for the session
 * @returns Trace priority view for the snapshot
 */
export function buildSnapshotTraceView(
  snapshot: PrioritySnapshot,
  _items: Item[],
  events: ArrangeAuditEvent[]
): TracePriorityView {
  const scoreSet = snapshot.scoreSet;

  // Find events related to this snapshot's tool run
  const snapshotEvents = events.filter((e) => {
    if (e.type === "ToolRunStarted" || e.type === "ToolRunCompleted" || e.type === "ToolRunFailed") {
      return e.toolName === snapshot.toolName;
    }
    if (e.type === "PrioritySnapshotCreated") {
      return e.scoreSet.toolRunId === scoreSet.toolRunId;
    }
    return false;
  });

  // Use the top-ranked item as representative for the trace view
  const topItem = scoreSet.scores
    .filter((s) => s.rank !== undefined)
    .sort((a, b) => (a.rank || 0) - (b.rank || 0))[0];

  if (!topItem) {
    // Fallback: use first item
    const firstItem = scoreSet.scores[0];
    if (firstItem) {
      return buildTracePriorityView(firstItem.itemId, scoreSet, snapshotEvents);
    }
    // Empty snapshot
    return {
      itemId: "",
      toolName: snapshot.toolName,
      eventChain: snapshotEvents,
      rawExplanation: {
        itemId: "",
        finalRank: null,
        finalScore: null,
        finalCategory: null,
        toolUsed: snapshot.toolName,
        toolConfig: {},
        criteriaSet: null,
        lens: null,
        rawInputs: {},
        eventChain: [],
      },
    };
  }

  return buildTracePriorityView(topItem.itemId, scoreSet, snapshotEvents);
}

/**
 * Gets executive views for all items in a snapshot.
 * 
 * @param snapshot - Priority snapshot
 * @param items - Items in the snapshot
 * @returns Map of itemId -> ExecutivePriorityView
 */
export function getItemExecutiveViews(
  snapshot: PrioritySnapshot,
  items: Item[]
): Map<string, ExecutivePriorityView> {
  const views = new Map<string, ExecutivePriorityView>();
  const scoreSet = snapshot.scoreSet;

  for (const score of scoreSet.scores) {
    const view = buildExecutivePriorityView(score.itemId, scoreSet);
    const item = items.find((i) => i.id === score.itemId);
    if (item) {
      view.label = item.label;
    }
    views.set(score.itemId, view);
  }

  return views;
}

/**
 * Gets trace views for all items in a snapshot.
 * 
 * @param snapshot - Priority snapshot
 * @param items - Items in the snapshot
 * @param events - Audit events for the session
 * @returns Map of itemId -> TracePriorityView
 */
export function getItemTraceViews(
  snapshot: PrioritySnapshot,
  _items: Item[],
  events: ArrangeAuditEvent[]
): Map<string, TracePriorityView> {
  const views = new Map<string, TracePriorityView>();
  const scoreSet = snapshot.scoreSet;

  // Find events related to this snapshot
  const snapshotEvents = events.filter((e) => {
    if (e.type === "ToolRunStarted" || e.type === "ToolRunCompleted" || e.type === "ToolRunFailed") {
      return e.toolName === snapshot.toolName;
    }
    if (e.type === "PrioritySnapshotCreated") {
      return e.scoreSet.toolRunId === scoreSet.toolRunId;
    }
    return false;
  });

  for (const score of scoreSet.scores) {
    const view = buildTracePriorityView(score.itemId, scoreSet, snapshotEvents);
    views.set(score.itemId, view);
  }

  return views;
}


